import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronDown, Loader2, Trophy, BarChart3, Flame, Calendar, Crown, Zap, Award, Filter, Rocket, HelpCircle, X, Layers } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

// Explications courtes et claires de chaque technique d'entraînement, pour que le
// client comprenne ce qu'il fait sans avoir à demander au coach.
const SET_TYPE_EXPLANATIONS = {
  straight: { fr: 'Vous faites toutes vos séries avec la même charge et le même nombre de répétitions, en vous reposant entre chaque. La méthode de base, la plus courante.', en: 'All sets done with the same weight and rep count, resting between each. The classic, most common method.' },
  superset: { fr: 'Vous enchaînez 2 exercices différents sans repos entre eux, puis vous récupérez. Permet de gagner du temps et d\'intensifier l\'effort.', en: 'Two different exercises done back-to-back with no rest, then you recover. Saves time and boosts intensity.' },
  biset: { fr: 'Comme le superset, mais les 2 exercices enchaînés ciblent le même muscle — pour le pousser à l\'épuisement.', en: 'Like a superset, but both exercises target the same muscle — to push it to exhaustion.' },
  triset: { fr: '3 exercices enchaînés sans repos, souvent sur le même muscle ou la même zone du corps.', en: '3 exercises done back-to-back with no rest, often for the same muscle or body area.' },
  giant_set: { fr: '4 exercices ou plus enchaînés sans repos. Très intense, réservé aux séances de finition.', en: '4 or more exercises chained with no rest. Very intense, used for finishing a workout.' },
  pyramid_up: { fr: 'Vous augmentez la charge à chaque série tout en diminuant le nombre de répétitions. Idéal pour progresser en force en toute sécurité.', en: 'You increase the weight each set while lowering the reps. Great for building strength safely.' },
  pyramid_down: { fr: 'Vous diminuez la charge à chaque série en augmentant le nombre de répétitions. Permet de bien fatiguer le muscle après un travail lourd.', en: 'You decrease the weight each set while raising the reps. Great for fatiguing the muscle after heavy work.' },
  drop_set: { fr: 'Dès que vous êtes fatigué, vous baissez immédiatement le poids (sans repos) pour continuer la série. Pousse le muscle jusqu\'à l\'échec total.', en: 'As soon as you\'re tired, you immediately drop the weight (no rest) to keep going. Pushes the muscle to full failure.' },
  rest_pause: { fr: 'Vous faites une courte pause de 10-15 secondes en pleine série pour repartir et faire encore quelques répétitions. Permet de dépasser l\'échec classique.', en: 'A short 10-15s pause mid-set lets you squeeze out a few more reps. Helps push past normal failure.' },
  cluster_set: { fr: 'Comme le rest-pause, mais avec plusieurs micro-pauses planifiées dans la série, pour soulever plus de charge au total.', en: 'Like rest-pause, but with several planned micro-pauses within the set, to lift more total weight.' },
  pre_exhaust: { fr: 'Vous fatiguez d\'abord un muscle isolément (ex: écarté) avant un exercice polyarticulaire (ex: développé) pour qu\'il travaille encore plus.', en: 'You first tire an isolated muscle (e.g. fly) before a compound exercise (e.g. bench press) so it works even harder.' },
  post_exhaust: { fr: 'Même principe que le pré-épuisement, mais l\'exercice d\'isolation est fait APRÈS l\'exercice polyarticulaire.', en: 'Same idea as pre-exhaust, but the isolation exercise comes AFTER the compound one.' },
  twenty_ones: { fr: '7 répétitions partielles basses + 7 partielles hautes + 7 complètes, sans repos. Technique d\'intensité classique, souvent aux biceps.', en: '7 partial low reps + 7 partial high reps + 7 full reps, no rest. Classic intensity technique, often for biceps.' },
  amrap: { fr: 'Vous faites le maximum de répétitions possible avec une charge donnée, jusqu\'à l\'échec.', en: 'You do as many reps as possible with a given weight, until failure.' },
  emom: { fr: 'Vous réalisez un nombre défini de répétitions au début de chaque minute, puis vous récupérez sur le temps restant. Très utilisé en cardio/fonctionnel.', en: 'You perform a set number of reps at the start of each minute, then rest for the remaining time. Common in cardio/functional training.' },
  tempo: { fr: 'Vous contrôlez précisément la vitesse du mouvement (ex: 3 secondes en descente). Augmente le temps sous tension et l\'intensité.', en: 'You precisely control the speed of the movement (e.g. 3s on the way down). Increases time under tension and intensity.' },
  myo_reps: { fr: 'Une 1ère série presque jusqu\'à l\'échec, suivie de plusieurs mini-séries de 3-5 reps avec très peu de repos. Très efficace pour prendre du muscle en peu de temps.', en: 'One set close to failure, followed by several mini-sets of 3-5 reps with very short rest. Very efficient for muscle growth in less time.' },
  circuit: { fr: 'Vous enchaînez plusieurs exercices différents à la suite avec peu ou pas de repos, souvent pour tout le corps. Idéal cardio + muscu.', en: 'Several different exercises done in a row with little or no rest, often full-body. Great cardio + strength combo.' },
  isometric: { fr: 'Vous maintenez une position sans bouger (ex: gainage, chaise) pendant un temps donné, sans mouvement des articulations.', en: 'You hold a position without moving (e.g. plank, wall sit) for a set time, with no joint movement.' },
  forced_reps: { fr: 'Une fois à l\'échec, un partenaire (ou le coach) vous aide légèrement à finir 1-2 répétitions supplémentaires.', en: 'Once at failure, a partner (or coach) gives you slight assistance to complete 1-2 extra reps.' },
};

const MUSCLE_GROUP_EN = {
  'Dos': 'Back', 'Pectoraux': 'Chest', 'Épaules': 'Shoulders', 'Biceps': 'Biceps',
  'Triceps': 'Triceps', 'Quadriceps': 'Quads', 'Ischio-jambiers': 'Hamstrings',
  'Fessiers': 'Glutes', 'Mollets': 'Calves', 'Abdominaux': 'Abs',
  'Avant-bras': 'Forearms', 'Full-Body / Cardio': 'Full-Body / Cardio', 'Autre': 'Other',
};
const tGroup = (g, en) => (en ? (MUSCLE_GROUP_EN[g] || g) : g);

const RM_BADGES = [
  { n: 1, icon: Crown, label: '1RM', sub: { fr: 'Charge max', en: 'Max load' }, grad: 'from-yellow-400 via-amber-500 to-orange-600', glow: 'rgba(251,191,36,0.55)' },
  { n: 6, icon: Trophy, label: '6RM', sub: { fr: 'Force', en: 'Strength' }, grad: 'from-fuchsia-500 via-purple-500 to-indigo-600', glow: 'rgba(192,38,211,0.5)' },
  { n: 10, icon: Flame, label: '10RM', sub: { fr: 'Hypertrophie', en: 'Hypertrophy' }, grad: 'from-orange-500 via-rose-500 to-red-600', glow: 'rgba(244,63,94,0.5)' },
  { n: 15, icon: Zap, label: '15RM', sub: { fr: 'Endurance', en: 'Endurance' }, grad: 'from-teal-400 via-cyan-500 to-blue-600', glow: 'rgba(34,211,238,0.5)' },
];

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-primary' }) => (
  <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-4">
    <Icon className={`w-5 h-5 mb-2 ${color}`} />
    <p className="text-2xl font-black text-white">{value}</p>
    <p className="text-xs text-zinc-500">{label}</p>
    {sub && <p className="text-[11px] text-zinc-600 mt-0.5">{sub}</p>}
  </div>
);

const ProgressBadge = ({ pct, en }) => {
  if (pct == null || pct < 3) return null;
  const L = (fr, eng) => (en ? eng : fr);
  const tier = pct >= 20 ? { grad: 'from-orange-500 to-red-600', icon: Flame } : pct >= 10 ? { grad: 'from-emerald-400 to-teal-500', icon: Rocket } : { grad: 'from-zinc-500 to-zinc-600', icon: Rocket };
  const Icon = tier.icon;
  return (
    <motion.span
      initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-gradient-to-r ${tier.grad} shrink-0`}
      data-testid="progress-badge-90d"
      title={L('Progression sur 90 jours', '90-day progress')}
    >
      <Icon className="w-2.5 h-2.5" /> +{pct}% · {L('3 mois', '3 mo')}
    </motion.span>
  );
};

const RmBadge = ({ badge, weight, en }) => {
  const Icon = badge.icon;
  return (
    <motion.div
      whileHover={{ scale: 1.06, y: -2 }}
      className={`relative rounded-xl bg-gradient-to-br ${badge.grad} p-[1.5px] overflow-hidden`}
      data-testid={`rm-badge-${badge.n}`}
    >
      <div className="rounded-[10px] bg-zinc-950/90 px-1.5 py-2 text-center overflow-hidden" style={{ boxShadow: `0 0 18px ${badge.glow}` }}>
        <div className="flex items-center justify-center gap-1">
          <Icon className="w-3 h-3 shrink-0" style={{ color: badge.glow.replace('0.5', '1').replace('0.55', '1') }} />
          <p className="text-[10px] font-mono font-bold text-zinc-300">{badge.label}</p>
        </div>
        <p className="text-base font-black text-white mt-0.5">{weight}<span className="text-[10px] text-zinc-500">kg</span></p>
        <p className="text-[8.5px] text-zinc-500 uppercase tracking-wide leading-tight break-words">{en ? badge.sub.en : badge.sub.fr}</p>
      </div>
    </motion.div>
  );
};

const RmGuideModal = ({ open, onClose, en, authFetch }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [setTypes, setSetTypes] = useState([]);
  const [openType, setOpenType] = useState(null);

  useEffect(() => {
    if (open && setTypes.length === 0) {
      authFetch('/api/tracking/programs-pro/set-types')
        .then((r) => r.json()).then((d) => setSetTypes(d.set_types || [])).catch(() => {});
    }
  }, [open, setTypes.length, authFetch]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose} data-testid="rm-guide-modal">
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-br from-zinc-900/90 to-black/90 backdrop-blur-xl border border-white/10 shadow-[0_0_35px_-10px_rgba(217,70,239,0.3)] rounded-3xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto relative"
          >
            <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white" data-testid="close-rm-guide-btn"><X className="w-5 h-5" /></button>
            <h3 className="text-xl font-black text-white mb-1 flex items-center gap-2"><HelpCircle className="w-5 h-5 text-primary" /> {L('Le guide du RM', 'The RM guide')}</h3>
            <p className="text-sm text-zinc-400 mb-5">{L("Comprendre les badges de force et le vocabulaire de vos séances.", "Understanding your strength badges and session vocabulary.")}</p>

            <p className="text-xs font-mono uppercase tracking-wider text-primary/80 mb-2">{L("Qu'est-ce qu'un RM ?", 'What is an RM?')}</p>
            <p className="text-sm text-zinc-300 mb-4 leading-relaxed">
              {L(
                "Le \u00abRM\u00bb (Répétition Maximale) est le poids maximal que vous pouvez soulever pour un nombre de répétitions donné. Nous l'estimons automatiquement (formule d'Epley) à partir de vos meilleures séries loggées, pour ne jamais vous demander de tester une vraie charge maximale (risque de blessure).",
                "The \u00abRM\u00bb (Rep Max) is the heaviest weight you can lift for a given number of reps. We estimate it automatically (Epley formula) from your best logged sets, so you never need to test a true 1-rep max (injury risk)."
              )}
            </p>
            <div className="grid grid-cols-2 gap-2 mb-5">
              {RM_BADGES.map((b) => (
                <div key={b.n} className={`rounded-xl bg-gradient-to-br ${b.grad} p-[1.5px]`}>
                  <div className="rounded-[10px] bg-zinc-950/95 p-3">
                    <div className="flex items-center gap-1.5 mb-1"><b.icon className="w-3.5 h-3.5 text-white" /><p className="text-xs font-black text-white">{b.label}</p></div>
                    <p className="text-[11px] text-zinc-400">
                      {b.n === 1 && L('Charge max — force neuromusculaire pure, 1 seule répétition possible.', 'Max load — pure neuromuscular strength, only 1 rep possible.')}
                      {b.n === 6 && L('Force — charges lourdes, 6 répétitions, développe la force brute.', 'Strength — heavy loads, 6 reps, builds raw strength.')}
                      {b.n === 10 && L('Hypertrophie — la zone idéale pour la prise de muscle.', 'Hypertrophy — the ideal zone for muscle growth.')}
                      {b.n === 15 && L('Endurance musculaire — charges plus légères, séries longues.', 'Muscular endurance — lighter loads, longer sets.')}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs font-mono uppercase tracking-wider text-primary/80 mb-2 flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> {L('Techniques utilisées dans vos programmes', 'Techniques used in your programs')}</p>
            <p className="text-[11px] text-zinc-500 mb-2.5">{L('Cliquez sur une technique pour comprendre ce qu\'elle signifie.', 'Click a technique to understand what it means.')}</p>
            {setTypes.length === 0 ? (
              <div className="flex justify-center py-6"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>
            ) : (
              <div className="space-y-1.5" data-testid="set-types-guide-list">
                {setTypes.map((t) => {
                  const isOpen = openType === t.value;
                  const explanation = SET_TYPE_EXPLANATIONS[t.value];
                  return (
                    <div key={t.value} className="bg-white/[0.03] rounded-lg overflow-hidden border border-white/5">
                      <button
                        onClick={() => setOpenType(isOpen ? null : t.value)}
                        className="w-full flex items-center justify-between gap-2 text-left text-sm text-zinc-300 px-3 py-2"
                        data-testid={`set-type-toggle-${t.value}`}
                      >
                        <span>{t.label}</span>
                        <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence initial={false}>
                        {isOpen && explanation && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <p className="text-xs text-zinc-400 leading-relaxed px-3 pb-2.5 border-t border-white/5 pt-2" data-testid={`set-type-explanation-${t.value}`}>
                              {en ? explanation.en : explanation.fr}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/** Espace Statistiques & Records : badges RM WAHOU (1/6/10/15) par exercice + filtre groupe musculaire + tendance de volume. */
export const WorkoutStats = ({ en, onBack, authFetch }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const [data, setData] = useState(null);
  const [group, setGroup] = useState('');
  const [guideOpen, setGuideOpen] = useState(false);

  useEffect(() => {
    const qs = group ? `?muscle_group=${encodeURIComponent(group)}` : '';
    authFetch(`/api/tracking/programs-pro/insights/stats${qs}`)
      .then((r) => r.json())
      .then(setData)
      .catch(() => setData({ locked: true }));
  }, [authFetch, group]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const { totals, weekly_trend: trend = [], exercises = [], muscle_groups = [] } = data;
  const trendPct = totals?.last_week_volume ? Math.round(((totals.this_week_volume - totals.last_week_volume) / totals.last_week_volume) * 100) : null;

  return (
    <div className="space-y-5" data-testid="workout-stats-view">
      {onBack && (
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="back-to-hub-btn">
          <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
        </button>
      )}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-black text-white flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" /> {L('Statistiques & Records', 'Stats & Records')}</h2>
        <button onClick={() => setGuideOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-white transition-colors" data-testid="open-rm-guide-btn">
          <HelpCircle className="w-4 h-4" /> {L('Le guide du RM', 'RM guide')}
        </button>
      </div>
      <RmGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} en={en} authFetch={authFetch} />

      {totals?.exercises_tracked === 0 && !group ? (
        <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-10 text-center text-zinc-500 text-sm" data-testid="stats-empty">
          {L('Réalisez votre première séance pour débloquer vos statistiques.', 'Complete your first session to unlock your stats.')}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard icon={Flame} label={L('Volume total soulevé', 'Total volume lifted')} value={`${Math.round(totals.all_time_volume).toLocaleString('fr-CH')} kg`} />
            <StatCard icon={Calendar} label={L('Séances réalisées', 'Sessions logged')} value={totals.sessions_count} />
            <StatCard icon={Award} label={L('Exercices suivis', 'Exercises tracked')} value={totals.exercises_tracked} color="text-yellow-400" />
            <StatCard icon={BarChart3} label={L('Volume cette semaine', 'This week volume')} value={`${Math.round(totals.this_week_volume)} kg`} sub={trendPct != null ? `${trendPct >= 0 ? '+' : ''}${trendPct}% ${L('vs semaine dernière', 'vs last week')}` : null} color={trendPct >= 0 ? 'text-emerald-400' : 'text-red-400'} />
          </div>

          {trend.length > 1 && (
            <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-4" data-testid="volume-trend-chart">
              <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">{L('Tendance de volume (8 dernières semaines)', 'Volume trend (last 8 weeks)')}</p>
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={trend}>
                  <defs>
                    <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a855f7" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} width={40} />
                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 12 }} formatter={(v) => [`${v} kg`, 'Volume']} />
                  <Area type="monotone" dataKey="volume" stroke="#a855f7" strokeWidth={2} fill="url(#volGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-xs font-mono uppercase tracking-wider text-zinc-500">{L('Records par exercice — Badges RM', 'Per-exercise records — RM Badges')}</p>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="muscle-group-filter">
                <Filter className="w-3.5 h-3.5 text-zinc-500" />
                <button onClick={() => setGroup('')} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${!group ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`} data-testid="muscle-filter-all">
                  {L('Tous', 'All')}
                </button>
                {muscle_groups.map((g) => (
                  <button key={g} onClick={() => setGroup(g)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-colors ${group === g ? 'bg-primary text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`} data-testid={`muscle-filter-${g}`}>
                    {tGroup(g, en)}
                  </button>
                ))}
              </div>
            </div>

            {exercises.length === 0 ? (
              <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-8 text-center text-zinc-500 text-sm" data-testid="stats-filtered-empty">
                {L('Aucun exercice suivi pour ce groupe musculaire.', 'No tracked exercise for this muscle group.')}
              </div>
            ) : (
              <div className="space-y-3">
                {exercises.map((ex, i) => (
                  <motion.div key={ex.name} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] p-4" data-testid={`exercise-rm-card-${i}`}>
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-bold">{ex.name}</p>
                          <ProgressBadge pct={ex.progress_pct_90d} en={en} />
                        </div>
                        <span className="text-[10px] font-mono uppercase tracking-wide text-primary/80">{tGroup(ex.muscle_group, en)}</span>
                      </div>
                      <span className="text-[11px] font-mono text-zinc-500">{L('Record', 'PR')}: <span className="text-primary font-bold">{ex.record_weight}kg × {ex.record_reps}</span></span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {RM_BADGES.map((b) => (
                        <RmBadge key={b.n} badge={b} weight={ex.rm_table[String(b.n)]} en={en} />
                      ))}
                    </div>
                    <p className="text-[11px] text-zinc-600 mt-2">{L('Volume total sur cet exercice', 'Total volume on this exercise')}: {Math.round(ex.total_volume).toLocaleString('fr-CH')} kg</p>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default WorkoutStats;
